import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import Loader from "../../components/spinner/Spinner";
import {
  fetchSubscriptionById,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
} from "../../services/subscription";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import "./subscriptions.scss";
import { formatDate, parsePaymentDate } from "../../utils/date";

const SubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isSidebar, setIsSidebar] = useState(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const paymentsPerPage = 10;

  useEffect(() => {
    loadSubscription();
  }, [id]);

  useEffect(() => {
    setPaymentPage(1);
  }, [payments.length]);

  const loadSubscription = async () => {
    try {
      const response = await fetchSubscriptionById(id);
      if (response.data.status) {
        setSubscription(response.data.subscription);
        const list = response.data.payments || [];
        const sorted = [...list].sort((a, b) => {
          const da = parsePaymentDate(
            a.payment_date || a.created_at || "",
          ).getTime();
          const db = parsePaymentDate(
            b.payment_date || b.created_at || "",
          ).getTime();
          return db - da;
        });
        setPayments(sorted);
      }
    } catch (error) {
      console.error("Failed to load subscription:", error);
      alert("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this subscription?"))
      return;

    setActionLoading(true);
    try {
      await cancelSubscription(id, false);
      alert("Subscription cancelled");
      loadSubscription();
    } catch (error) {
      alert("Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      await pauseSubscription(id);
      alert("Subscription paused");
      loadSubscription();
    } catch (error) {
      alert("Failed to pause subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await resumeSubscription(id);
      alert("Subscription resumed");
      loadSubscription();
    } catch (error) {
      alert("Failed to resume subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const getNextDueDate = () => {
    if (!subscription) return "-";

    if (subscription.charge_at) {
      return formatDate(subscription.charge_at);
    }
    if (subscription.current_end) {
      return formatDate(subscription.current_end);
    }
    if (
      subscription.created_at &&
      (subscription.status === "active" || subscription.status === "created")
    ) {
      const createdDate = new Date(subscription.created_at);
      const nextDue = new Date(createdDate);
      nextDue.setMonth(nextDue.getMonth() + (subscription.paid_count || 0) + 1);
      return formatDate(nextDue);
    }
    return "-";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const paymentPagination = {
    total: payments.length,
    totalPages: Math.max(1, Math.ceil(payments.length / paymentsPerPage)),
  };

  const paymentStart = (paymentPage - 1) * paymentsPerPage;
  const paymentEnd = Math.min(paymentStart + paymentsPerPage, payments.length);
  const pagedPayments = payments.slice(paymentStart, paymentEnd);

  const handlePaymentPrevPage = () => {
    setPaymentPage((current) => Math.max(current - 1, 1));
  };

  const handlePaymentNextPage = () => {
    setPaymentPage((current) =>
      Math.min(current + 1, paymentPagination.totalPages),
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: "Active", className: "status-active" },
      authenticated: { label: "Pending", className: "status-pending" },
      created: { label: "Created", className: "status-created" },
      paused: { label: "Paused", className: "status-paused" },
      cancelled: { label: "Cancelled", className: "status-cancelled" },
      completed: { label: "Completed", className: "status-completed" },
      halted: { label: "Halted", className: "status-halted" },
      pending: { label: "Pending", className: "status-pending" },
    };
    const c = config[status] || config.pending;
    return <span className={`statusBadge ${c.className}`}>{c.label}</span>;
  };

  return (
    <div className="list">
      <Sidebar isSidebar={isSidebar} />
      <div className="listContainer">
        <Navbar isSidebar={isSidebar} setIsSidebar={setIsSidebar} />

        <div className="subscriptionDetailContainer">
          <button
            onClick={() => navigate("/subscriptions")}
            className="backButton"
          >
            <ArrowBackIcon fontSize="small" /> Back to Subscriptions
          </button>

          {loading ? (
            <Loader />
          ) : !subscription ? (
            <div className="emptyState">
              <h3>Subscription Not Found</h3>
              <button onClick={() => navigate("/subscriptions")}>
                Back to List
              </button>
            </div>
          ) : (
            <>
              <div className="detailHeader">
                <h1>
                  Subscription #{subscription.id}
                  {getStatusBadge(subscription.status)}
                </h1>
              </div>

              <div className="detailGrid">
                {/* Subscription Info */}
                <div className="detailCard">
                  <h3>
                    <DescriptionOutlinedIcon fontSize="small" /> Subscription
                    Details
                  </h3>
                  <div className="infoRow">
                    <span className="label">Razorpay ID</span>
                    <span className="value">
                      {subscription.razorpay_subscription_id}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Monthly Amount</span>
                    <span className="value">
                      {formatCurrency(subscription.amount)}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Total Duration</span>
                    <span className="value">
                      {subscription.total_count} months
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Payments Made</span>
                    <span className="value">
                      {subscription.paid_count || 0}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Remaining</span>
                    <span className="value">
                      {subscription.remaining_count || 0}
                    </span>
                  </div>
                  <div className="infoRow highlight">
                    <span className="label">Next Due Date</span>
                    <span className="value nextDue">{getNextDueDate()}</span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Current Period</span>
                    <span className="value">
                      {formatDate(subscription.current_start)} -{" "}
                      {formatDate(subscription.current_end)}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Created</span>
                    <span className="value">
                      {formatDate(subscription.created_at)}
                    </span>
                  </div>
                </div>

                {/* Kid Info */}
                <div className="detailCard">
                  <h3>
                    <ChildCareOutlinedIcon fontSize="small" /> Sponsored Child
                  </h3>
                  <div className="infoRow">
                    <span className="label">Name</span>
                    <span className="value">{subscription.kid_name}</span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Kid ID</span>
                    <span className="value">#{subscription.kid_id}</span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Category</span>
                    <span className="value">
                      {subscription.category_name || "General"}
                    </span>
                  </div>
                  {subscription.kid_bio && (
                    <div className="infoRow">
                      <span className="label">Bio</span>
                      <span className="value">{subscription.kid_bio}</span>
                    </div>
                  )}
                </div>

                {/* Sponsor Info */}
                <div className="detailCard">
                  <h3>
                    <PersonOutlineIcon fontSize="small" /> Sponsor Details
                  </h3>
                  <div className="infoRow">
                    <span className="label">Name</span>
                    <span className="value">
                      {subscription.is_anonymous
                        ? "Anonymous Sponsor"
                        : subscription.mentor_name ||
                          subscription.customer_name ||
                          "-"}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Email</span>
                    <span className="value">{subscription.customer_email}</span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Phone</span>
                    <span className="value">{subscription.customer_phone}</span>
                  </div>
                  {subscription.mentor_id && (
                    <div className="infoRow">
                      <span className="label">User ID</span>
                      <span className="value">#{subscription.mentor_id}</span>
                    </div>
                  )}
                  <div className="infoRow">
                    <span className="label">Anonymous</span>
                    <span className="value">
                      {subscription.is_anonymous ? "Yes" : "No"}
                    </span>
                  </div>
                </div>

                {/* Razorpay Info */}
                <div className="detailCard">
                  <h3>
                    <PaymentOutlinedIcon fontSize="small" /> Razorpay Details
                  </h3>
                  <div className="infoRow">
                    <span className="label">Subscription ID</span>
                    <span
                      className="value"
                      style={{ fontSize: "12px", wordBreak: "break-all" }}
                    >
                      {subscription.razorpay_subscription_id}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Customer ID</span>
                    <span
                      className="value"
                      style={{ fontSize: "12px", wordBreak: "break-all" }}
                    >
                      {subscription.razorpay_customer_id}
                    </span>
                  </div>
                  <div className="infoRow">
                    <span className="label">Plan ID</span>
                    <span
                      className="value"
                      style={{ fontSize: "12px", wordBreak: "break-all" }}
                    >
                      {subscription.razorpay_plan_id}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div className="paymentsTable">
                <h3>
                  <ReceiptLongOutlinedIcon fontSize="small" /> Payment History
                </h3>
                {payments.length === 0 ? (
                  <p
                    style={{
                      color: "#888",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    No payments recorded yet
                  </p>
                ) : (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <th>Payment ID</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Method</th>
                          <th>Billing Period</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPayments.map((payment) => (
                          <tr key={payment.id}>
                            <td style={{ fontSize: "12px" }}>
                              {payment.razorpay_payment_id}
                            </td>
                            <td>{formatCurrency(payment.amount / 100)}</td>
                            <td>
                              <span
                                className={`statusBadge ${
                                  payment.status === "captured"
                                    ? "status-active"
                                    : "status-cancelled"
                                }`}
                              >
                                {payment.status}
                              </span>
                            </td>
                            <td>{payment.method || "-"}</td>
                            <td>
                              {formatDate(payment.billing_start)} -{" "}
                              {formatDate(payment.billing_end)}
                            </td>
                            <td>{formatDate(payment.payment_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="pagination">
                      <button
                        type="button"
                        onClick={handlePaymentPrevPage}
                        disabled={paymentPage <= 1}
                      >
                        Previous
                      </button>
                      <span>
                        Showing {paymentStart + 1}-{paymentEnd} of{" "}
                        {paymentPagination.total}
                      </span>
                      <button
                        type="button"
                        onClick={handlePaymentNextPage}
                        disabled={paymentPage >= paymentPagination.totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              {(subscription.status === "active" ||
                subscription.status === "paused") && (
                <div className="actionButtons">
                  {subscription.status === "active" && (
                    <>
                      <button
                        onClick={handlePause}
                        disabled={actionLoading}
                        className="pauseBtn"
                      >
                        <PauseCircleOutlinedIcon fontSize="small" />
                        {actionLoading ? "Processing..." : "Pause Subscription"}
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={actionLoading}
                        className="cancelBtn"
                      >
                        <CancelOutlinedIcon fontSize="small" />
                        {actionLoading
                          ? "Processing..."
                          : "Cancel Subscription"}
                      </button>
                    </>
                  )}
                  {subscription.status === "paused" && (
                    <>
                      <button
                        onClick={handleResume}
                        disabled={actionLoading}
                        className="resumeBtn"
                      >
                        <PlayCircleOutlinedIcon fontSize="small" />
                        {actionLoading
                          ? "Processing..."
                          : "Resume Subscription"}
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={actionLoading}
                        className="cancelBtn"
                      >
                        <CancelOutlinedIcon fontSize="small" />
                        {actionLoading
                          ? "Processing..."
                          : "Cancel Subscription"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetail;
